import qs from 'qs';

import { GridFilterItem } from '@mui/x-data-grid/models/gridFilterItem';

import { operations } from '@luxuryescapes/contract-svc-public-offer-feed/generated';

import { json_headers, request } from '~/services/common';

const getDefaultRuleDefinitions = (primaryMetrics: Array<string>) => {
  return {
    [primaryMetrics[0]]: {
      H: 0,
      L: 0,
      M: 0,
    },
    [primaryMetrics[1]]: {
      H: 0,
      L: 0,
      M: 0,
    },
    [primaryMetrics[2]]: {
      H: 0,
      L: 0,
      M: 0,
    },
  };
};

export const getDefaultRuleClassifications = () => {
  return {
    H_H_H: 0,
    H_H_L: 0,
    H_H_M: 0,
    H_L_H: 0,
    H_L_L: 0,
    H_L_M: 0,
    H_M_H: 0,
    H_M_L: 0,
    H_M_M: 0,
    L_H_H: 0,
    L_H_L: 0,
    L_H_M: 0,
    L_L_H: 0,
    L_L_L: 0,
    L_L_M: 0,
    L_M_H: 0,
    L_M_L: 0,
    L_M_M: 0,
    M_H_H: 0,
    M_H_L: 0,
    M_H_M: 0,
    M_L_H: 0,
    M_L_L: 0,
    M_L_M: 0,
    M_M_H: 0,
    M_M_L: 0,
    M_M_M: 0,
  };
};

export type AdGroupResponse = operations['googleAdsGetAdGroupsV2']['responses']['200']['content']['application/json'];

export type AdCampaign = {
  id: string;
  name: string;
  campaignBudget: CampaignBudget;
};

export type AdCustomer = {
  id: string;
  descriptiveName: string;
  campaigns: Array<AdCampaign>;
};

export type Campaign =
  operations['googleAdsCustomers']['responses']['200']['content']['application/json']['result']['0']['campaigns']['0'];

export type CampaignBudget =
  operations['googleAdsCustomers']['responses']['200']['content']['application/json']['result']['0']['campaigns']['0']['campaignBudget'];

function basePath() {
  return window.configs.API_HOST + '/api/public-offer-feed';
}

export type GetAdGroupProps = {
  customerId: string;
  campaignIds: Array<string>;
  pageToken?: string;
  adGroupIds?: Array<string>;
};

export async function getAdGroups({
  customerId,
  campaignIds,
  pageToken,
  adGroupIds,
}: GetAdGroupProps): Promise<AdGroupResponse> {
  const queryParams = {
    pageToken,
    campaignIds: campaignIds.join(','),
    adGroupIds: adGroupIds.join(','),
  };
  const url = `${basePath()}/google-ads/${customerId}/campaigns/ad-groups?${qs.stringify(queryParams)}`;
  return request(url, {
    method: 'GET',
  });
}

export async function mutateAdgroup(
  customerId: string,
  adGroupId: string,
  resourceName: string,
  targetCPA: number,
): Promise<void> {
  const url = `${basePath()}/google-ads/${customerId}/ad-group/${adGroupId}`;
  return request(url, {
    method: 'PATCH',
    body: JSON.stringify({ resourceName, targetCPA }),
    headers: json_headers,
  });
}

type UpdateCampaignBudgetProps = {
  customerId: string;
  campaignId: string;
  payload: operations['GoogleAdsUpdateCampaignBudget']['parameters']['body']['payload'];
};

export async function updateCampaignBudget({
  customerId,
  campaignId,
  payload,
}: UpdateCampaignBudgetProps): Promise<void> {
  const url = `${basePath()}/google-ads/${customerId}/campaigns/${campaignId}/budgets`;
  return request(url, {
    method: 'PUT',
    body: JSON.stringify(payload),
    headers: json_headers,
  });
}

export async function getCustomers(): Promise<{ result: Array<AdCustomer> }> {
  const url = `${basePath()}/google-ads/customers`;
  return request(url, {
    method: 'GET',
    headers: json_headers,
  });
}

type ModificationResponse = operations['getAllModifications']['responses']['200']['content']['application/json'];

export type ModificationLog = ModificationResponse['result']['0'];

export type ModificationFilters = {
  adGroupIds?: Array<string>;
  customerId: string;
  campaignIds?: Array<string>;
  limit: number;
  page: number;
};

export async function getModifications(params: ModificationFilters): Promise<ModificationResponse> {
  const query: operations['getAllModifications']['parameters']['query'] = {
    campaignIds: params.campaignIds?.join(','),
    limit: params.limit,
    page: params.page,
  };
  const queryParams = `?${qs.stringify(query)}`;
  const url = `${basePath()}/google-ads/modifications${queryParams}`;
  return request(url, {
    method: 'GET',
    headers: json_headers,
  });
}

// ========== Google Ads Campaigns Config =========

type GoogleAdsCampaignsListResponse =
  operations['listGoogleAdsCampaignsConfig']['responses']['200']['content']['application/json'];

export type GoogleAdsCampaignConfigItem = GoogleAdsCampaignsListResponse['result']['campaigns'][0];

export async function listGoogleAdsCampaignsConfig({
  page,
  limit,
  customerId,
  campaignId,
  campaignType,
  enabled,
}: {
  page?: number;
  limit?: number;
  customerId?: string;
  campaignId?: string;
  campaignType?: 'offer' | 'search';
  enabled?: boolean;
}): Promise<GoogleAdsCampaignsListResponse> {
  const query = qs.stringify({
    page,
    limit,
    customer_id: customerId,
    campaign_id: campaignId,
    campaign_type: campaignType,
    enabled: enabled,
  });
  const url = `${basePath()}/google-ads/campaigns-config?${query}`;
  return request(url, {
    method: 'GET',
    headers: json_headers,
  });
}

export async function createGoogleAdsCampaignConfig(payload: {
  customer_id: string;
  campaign_id: string;
  campaign_type: 'offer' | 'search';
  enabled: boolean;
}): Promise<operations['createGoogleAdsCampaignConfig']['responses']['201']['content']['application/json']> {
  const url = `${basePath()}/google-ads/campaigns-config`;
  return request(url, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: json_headers,
  });
}

export async function updateGoogleAdsCampaignConfig(
  campaignId: string,
  payload: { customer_id: string; campaign_type: 'offer' | 'search'; enabled: boolean },
): Promise<operations['updateGoogleAdsCampaignConfig']['responses']['200']['content']['application/json']> {
  const url = `${basePath()}/google-ads/campaigns-config/${campaignId}`;
  return request(url, {
    method: 'PUT',
    body: JSON.stringify(payload),
    headers: json_headers,
  });
}

export async function createNewVid(data, offerId) {
  return request(`${basePath()}/video/${offerId}`, {
    method: 'POST',
    credentils: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deleteGoogleAdsCampaignConfig(
  campaignId: string,
): Promise<operations['deleteGoogleAdsCampaignConfig']['responses']['200']['content']['application/json']> {
  const url = `${basePath()}/google-ads/campaigns-config/${campaignId}`;
  return request(url, {
    method: 'DELETE',
    headers: json_headers,
  });
}

export async function getVidByOfferId(offerId) {
  return request(`${basePath()}/video/${offerId}`, {
    method: 'GET',
    credentils: 'include',
  });
}

export type AdGroupRuleDefinitionResult =
  operations['getAdGroupRuleDefinitions']['responses']['200']['content']['application/json'];
export type AdGroupRuleDefinition = AdGroupRuleDefinitionResult['result'][0];

export async function getAdGroupRuleDefinitions({
  page,
  limit,
}: {
  page: number;
  limit: number;
}): Promise<AdGroupRuleDefinitionResult> {
  const queryParams = {
    page,
    limit,
  };
  const url = `${basePath()}/google-ads/rule-definitions?${qs.stringify(queryParams)}`;
  return request(url, {
    method: 'GET',
  });
}

export type BidOverview = operations['getBidOverview']['responses']['200']['content']['application/json']['result'];
export type RuleDefinitionData = BidOverview['rule_definitions'];
export type BidClassifications = BidOverview['bid_classifications'];
export async function getAdGroupBidOverview(adGroupId: string): Promise<{ result: BidOverview }> {
  const url = `${basePath()}/google-ads/${adGroupId}/bid-overview`;
  return request(url, {
    method: 'GET',
  });
}

export async function updateAdGroupBidClassifications(
  adGroupId: string,
  bidClassifications: BidClassifications,
): Promise<void> {
  const body = {
    bid_classifications: bidClassifications,
  };
  const url = `${basePath()}/google-ads/${adGroupId}/bid-classification`;
  return request(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function updateAdGroupRuleDefinitions(
  adGroupId: string,
  adGroupName: string,
  ruleDefinitions: RuleDefinitionData,
): Promise<void> {
  const body = [
    {
      ad_group_id: adGroupId,
      ad_group_name: adGroupName,
      spend: ruleDefinitions.spend,
      margin: ruleDefinitions.margin,
      mpv: ruleDefinitions.mpv,
    },
  ];
  const url = `${basePath()}/google-ads/rule-definitions`;
  return request(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function createCampaignRules(
  campaignId: string,
  primaryMetrics: Array<string>,
  adGroupId?: string,
  ruleDefinitions?: RuleDefinitionData,
  ruleClassifications?: BidClassifications,
): Promise<void> {
  const body = {
    campaign_id: +campaignId,
    ad_group_id: adGroupId ? +adGroupId : null,
    primary_metrics: primaryMetrics,
    rule_definitions: ruleDefinitions ?? getDefaultRuleDefinitions(primaryMetrics),
    rule_classifications: ruleClassifications ?? getDefaultRuleClassifications(),
  };
  const url = `${basePath()}/google-ads/rules`;
  return request(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function getRules(campaignId: string, adGroupId?: string) {
  const url = `${basePath()}/google-ads/campaign/${campaignId}/ad-group/${adGroupId}/rules`;
  return request(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function updateRuleDefinitions({
  campaignId,
  adGroupId,
  ruleDefinitions,
}: {
  campaignId: string;
  adGroupId?: string;
  ruleDefinitions: RuleDefinitionData;
}): Promise<void> {
  const body = {
    rule_definitions: ruleDefinitions,
  };

  let url;
  if (adGroupId) {
    url = `${basePath()}/google-ads/campaign/${campaignId}/ad-group/${adGroupId}/rules`;
  } else {
    url = `${basePath()}/google-ads/campaign/${campaignId}/rules`;
  }

  return request(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function updateRuleClassifications({
  campaignId,
  adGroupId,
  ruleClassifications,
}: {
  campaignId: string;
  adGroupId?: string;
  ruleClassifications: BidClassifications;
}): Promise<void> {
  const body = {
    rule_classifications: ruleClassifications,
  };

  let url;
  if (adGroupId) {
    url = `${basePath()}/google-ads/campaign/${campaignId}/ad-group/${adGroupId}/rules`;
  } else {
    url = `${basePath()}/google-ads/campaign/${campaignId}/rules`;
  }

  return request(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function deleteAllCustomRules(campaignId: string): Promise<void> {
  const url = `${basePath()}/google-ads/campaign/${campaignId}/rules`;
  return request(url, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
}

type AdGroupListResponse = operations['getAdGroupsByCampaign']['responses']['200']['content']['application/json'];
export type AdGroupListItem = AdGroupListResponse['result'][0];

export async function getAdGroupsByCampaign(
  customerId: string,
  campaignId: string,
  limit: number,
  page: number,
  search?: string,
  sortModel?: { field: string; sort: string },
  filters?: Array<GridFilterItem>,
): Promise<AdGroupListResponse> {
  const queryParams = {
    limit,
    page,
    search,
    orderBy: sortModel?.field,
    orderDirection: sortModel?.sort,
    filters: JSON.stringify(filters),
  };
  const url = `${basePath()}/google-ads/customer/${customerId}/campaign/${campaignId}/ad-groups?${qs.stringify(
    queryParams,
  )}`;
  return request(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
}

type AdGroupDetailResponse = operations['getAdGroup']['responses']['200']['content']['application/json'];
export type AdGroupDetails = AdGroupDetailResponse['result'];
export async function getAdGroup(
  customerId: string,
  campaignId: string,
  adGroupId: string,
): Promise<AdGroupDetailResponse> {
  const url = `${basePath()}/google-ads/customer/${customerId}/campaign/${campaignId}/ad-group/${adGroupId}`;
  return request(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function deleteCustomRule(campaignId: string, adGroupId: string): Promise<void> {
  const url = `${basePath()}/google-ads/campaign/${campaignId}/ad-group/${adGroupId}/rules`;
  return request(url, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function publishAdGroupBid(customerId: string, campaignId: string, adGroupId: string): Promise<void> {
  const url = `${basePath()}/google-ads/customer/${customerId}/campaign/${campaignId}/ad-group/${adGroupId}/publish`;
  return request(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
}

type BidHistoryResponse = operations['bidModificationHistory']['responses']['200']['content']['application/json'];
export type BidHistoryRecord = BidHistoryResponse['result'][0];

export async function getAdGroupBidHistory({
  customerId,
  campaignId,
  adGroupId,
  search,
  page,
  limit,
}: {
  customerId: string;
  campaignId: string;
  adGroupId: string;
  search?: string;
  page: number;
  limit: number;
}): Promise<BidHistoryResponse> {
  const query = qs.stringify({
    campaignId,
    adGroupId,
    customerId,
    search,
    page,
    limit,
  });

  const url = `${basePath()}/google-ads/bid-history?${query}`;
  return request(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function updatePrimaryMetrics(
  campaignId: string,
  primaryMetrics: Array<string>,
  adGroupId?: string,
): Promise<void> {
  const body = {
    primary_metrics: primaryMetrics,
    rule_definitions: getDefaultRuleDefinitions(primaryMetrics),
    rule_classifications: getDefaultRuleClassifications(),
  };

  let url;
  if (adGroupId) {
    url = `${basePath()}/google-ads/campaign/${campaignId}/ad-group/${adGroupId}/rules`;
  } else {
    url = `${basePath()}/google-ads/campaign/${campaignId}/rules`;
  }

  return request(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export type BulkCreateOrUpdateRulesPayload = {
  campaignId: number;
  adGroupIds: Array<number>;
  ruleDefinitions?: RuleDefinitionData;
  ruleClassifications?: BidClassifications;
};

export async function bulkCreateOrUpdateRules(payload: BulkCreateOrUpdateRulesPayload): Promise<void> {
  const url = `${basePath()}/google-ads/rules`;
  return request(url, {
    method: 'PUT',
    body: JSON.stringify(payload),
    headers: json_headers,
  });
}

// ========== Ad Groups Tags =========

export type Tag = operations['searchTags']['responses']['200']['content']['application/json']['result'][0];

export async function searchTags(customerId: string, campaignId: string): Promise<{ result: Array<Tag> }> {
  const url = `${basePath()}/google-ads/customer/${customerId}/campaign/${campaignId}/tags`;
  return request(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function updateAdGroupTags(
  customerId: string,
  campaignId: string,
  adGroupId: string,
  tags: Array<string>,
): Promise<void> {
  const url = `${basePath()}/google-ads/customer/${customerId}/campaign/${campaignId}/ad-group/${adGroupId}/tags`;
  return request(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tags }),
  });
}

export async function createOrAssignTags({
  tags,
}: {
  tags: Array<{
    customerId: string;
    campaignId: string;
    adGroupId?: string;
    tagId?: string;
    tagName: string;
  }>;
}): Promise<void> {
  const url = `${basePath()}/google-ads/tags`;
  return request(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tags }),
  });
}

export async function removeTagForAdGroups(
  customerId: string,
  campaignId: string,
  tagId: string,
  adGroupIds: Array<string>,
): Promise<void> {
  const url = `${basePath()}/google-ads/customer/${customerId}/campaign/${campaignId}/tags`;
  return request(url, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tagId, adGroupIds }),
  });
}

export async function deleteTag(tagId: string): Promise<void> {
  const url = `${basePath()}/google-ads/tags/${tagId}`;
  return request(url, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
}

type HotelGroupViewResponse = operations['getHotelGroupView']['responses']['200']['content']['application/json'];
export type HotelGroupViewRecord = HotelGroupViewResponse['result'][0];

export async function fetchHotelGroupView({
  customerId,
  propertyIds = [],
  page,
  limit,
}: {
  customerId: string;
  propertyIds?: Array<string>;
  page?: number;
  limit?: number;
}): Promise<HotelGroupViewResponse> {
  const query = qs.stringify({
    page,
    limit,
  });
  const url = `${basePath()}/google-ads/hotel-bidding/customer/${customerId}/hotels?${query}`;
  const payload = { propertyIds };
  return request(url, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: json_headers,
  });
}

type HotelCriteriaUpdateItem = operations['updateHotelGroupCriteria']['parameters']['body']['payload']['updates'][0];

export async function updateHotelGroupCriteria({
  customerId,
  updates,
}: {
  customerId: string;
  updates: Array<HotelCriteriaUpdateItem>;
}): Promise<void> {
  const url = `${basePath()}/google-ads/hotel-bidding/customer/${customerId}/hotel-group-criteria`;
  const payload = {
    updates,
  };
  return request(url, {
    method: 'PUT',
    body: JSON.stringify(payload),
    headers: json_headers,
  });
}

type HotelBidHistoryResponse =
  operations['getHotelBidModificationHistory']['responses']['200']['content']['application/json'];
export type HotelBidHistoryRecord = HotelBidHistoryResponse['result'][0];

export async function getHotelBidHistory({
  customerId,
  campaignId,
  adGroupId,
  hotelId,
  search,
  page,
  limit,
}: {
  customerId: string;
  campaignId?: string;
  adGroupId?: string;
  hotelId?: string;
  search?: string;
  page?: number;
  limit?: number;
}): Promise<HotelBidHistoryResponse> {
  const query = qs.stringify({
    campaignId,
    adGroupId,
    customerId,
    hotelId,
    search,
    page,
    limit,
  });

  const url = `${basePath()}/google-ads/hotel-bidding/customer/${customerId}/history?${query}`;
  return request(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function uploadHotelBiddingCriteria(customerId: string, file: File): Promise<void> {
  const url = `${basePath()}/google-ads/hotel-bidding/customer/${customerId}/upload-csv`;

  const formData = new FormData();
  formData.append('file', file);

  return request(url, {
    method: 'POST',
    body: formData,
    headers: {
      Accept: 'application/json',
    },
  });
}

type DeleteCriteriaItem = operations['deleteHotelGroupCriteria']['parameters']['body']['payload']['items'][0];
export async function deleteHotelGroupCriteria({
  customerId,
  items,
}: {
  customerId: string;
  items: Array<DeleteCriteriaItem>;
}): Promise<void> {
  const url = `${basePath()}/google-ads/hotel-bidding/customer/${customerId}/hotel-group-criteria`;
  const payload = {
    items,
  };
  return request(url, {
    method: 'DELETE',
    body: JSON.stringify(payload),
    headers: json_headers,
  });
}

// Forced Rankings Config types
export type ForcedRankingsConfigListResponse =
  operations['listForcedRankingsConfigs']['responses']['200']['content']['application/json'];

export type ForcedRankingsConfigListParams = operations['listForcedRankingsConfigs']['parameters']['query'];

export type ForcedRankingsConfigCreateResponse =
  operations['createForcedRankingsConfig']['responses']['201']['content']['application/json'];

export type ForcedRankingsConfigCreateParams =
  operations['createForcedRankingsConfig']['parameters']['body']['payload'];

export type ForcedRankingsConfigUpdateResponse =
  operations['updateForcedRankingsConfig']['responses']['200']['content']['application/json'];

export type ForcedRankingsConfigUpdateParams =
  operations['updateForcedRankingsConfig']['parameters']['body']['payload'];

export type ForcedRankingsConfigDeleteResponse =
  operations['deleteForcedRankingsConfig']['responses']['200']['content']['application/json'];

export type ForcedRankingsConfig =
  operations['createForcedRankingsConfig']['responses']['201']['content']['application/json']['result'];

// Forced Rankings Config API methods
export async function listForcedRankingsConfigs({
  page = 0,
  limit = 20,
  country_code,
  distance_lookup,
  brand,
}: ForcedRankingsConfigListParams): Promise<ForcedRankingsConfigListResponse> {
  const queryParams = {
    page,
    limit,
    brand,
    ...(country_code && { country_code }),
    ...(distance_lookup && { distance_lookup }),
  };

  const url = `${basePath()}/forced-rankings-config?${qs.stringify(queryParams)}`;
  return request(url, {
    method: 'GET',
  });
}

export async function createForcedRankingsConfig({
  brand,
  country_code,
  distance_lookup,
  user_regions,
}: ForcedRankingsConfigCreateParams): Promise<ForcedRankingsConfigCreateResponse> {
  const url = `${basePath()}/forced-rankings-config`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({
      country_code,
      distance_lookup,
      user_regions,
      brand,
    }),
  });
}

export async function updateForcedRankingsConfig(
  { country_code, distance_lookup, user_regions, brand }: ForcedRankingsConfigUpdateParams,
  id: string,
): Promise<ForcedRankingsConfigUpdateResponse> {
  const url = `${basePath()}/forced-rankings-config/${id}`;
  return request(url, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify({
      distance_lookup,
      user_regions,
      country_code,
      brand,
    }),
  });
}

export async function deleteForcedRankingsConfig(id: string): Promise<ForcedRankingsConfigDeleteResponse> {
  const url = `${basePath()}/forced-rankings-config/${id}`;
  return request(url, {
    method: 'DELETE',
  });
}

// Pinned Offers Config API types
export type PinnedOffersConfigListParams = operations['listPinnedOffersConfigs']['parameters']['query'];
export type PinnedOffersConfigListResponse =
  operations['listPinnedOffersConfigs']['responses']['200']['content']['application/json'];
export type PinnedOffersConfigCreateParams = operations['createPinnedOffersConfig']['parameters']['body']['payload'];
export type PinnedOffersConfigCreateResponse =
  operations['createPinnedOffersConfig']['responses']['201']['content']['application/json'];
export type PinnedOffersConfigUpdateParams = operations['updatePinnedOffersConfig']['parameters']['body']['payload'];
export type PinnedOffersConfigUpdateResponse =
  operations['updatePinnedOffersConfig']['responses']['200']['content']['application/json'];
export type PinnedOffersConfigDeleteResponse =
  operations['deletePinnedOffersConfig']['responses']['200']['content']['application/json'];
export type PinnedOffersConfig =
  operations['createPinnedOffersConfig']['responses']['201']['content']['application/json']['result'];

// Pinned Offers Config API methods
export async function listPinnedOffersConfigs({
  page = 0,
  limit = 20,
  offer_country,
  distance_lookup,
  brand,
}: PinnedOffersConfigListParams): Promise<PinnedOffersConfigListResponse> {
  const queryParams = {
    page,
    limit,
    ...(offer_country && { offer_country }),
    ...(distance_lookup && { distance_lookup }),
    brand,
  };

  const url = `${basePath()}/pinned-offers-config?${qs.stringify(queryParams)}`;
  return request(url, {
    method: 'GET',
  });
}

export async function createPinnedOffersConfig({
  offer_country,
  distance_lookup,
  user_regions,
  brand,
}: PinnedOffersConfigCreateParams): Promise<PinnedOffersConfigCreateResponse> {
  const url = `${basePath()}/pinned-offers-config`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({
      offer_country,
      distance_lookup,
      user_regions,
      brand,
    }),
  });
}

export async function updatePinnedOffersConfig(
  { user_regions, offer_country, distance_lookup, brand }: PinnedOffersConfigUpdateParams,
  id: string,
): Promise<PinnedOffersConfigUpdateResponse> {
  const url = `${basePath()}/pinned-offers-config/${id}`;
  return request(url, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify({
      user_regions,
      offer_country,
      distance_lookup,
      brand,
    }),
  });
}

export async function deletePinnedOffersConfig(id: string): Promise<PinnedOffersConfigDeleteResponse> {
  const url = `${basePath()}/pinned-offers-config/${id}`;
  return request(url, {
    method: 'DELETE',
  });
}

// Pinned Offers API types
export type PinnedOffersListParams = operations['listPinnedOffers']['parameters']['query'];
export type PinnedOffersListResponse =
  operations['listPinnedOffers']['responses']['200']['content']['application/json'];
export type PinnedOffersCreateParams = operations['createPinnedOffer']['parameters']['body']['payload'];
export type PinnedOffersCreateResponse =
  operations['createPinnedOffer']['responses']['201']['content']['application/json'];
export type PinnedOffersUpdateParams = operations['updatePinnedOffer']['parameters']['body']['payload'];
export type PinnedOffersUpdateResponse =
  operations['updatePinnedOffer']['responses']['200']['content']['application/json'];
export type PinnedOffersDeleteResponse =
  operations['deletePinnedOffer']['responses']['204']['content']['application/json'];
export type PinnedOffer = operations['createPinnedOffer']['responses']['201']['content']['application/json']['result'];

// Pinned Offers API methods
export async function listPinnedOffers({
  page = 0,
  limit = 20,
  offer_country,
  offer_id,
  brand,
}: PinnedOffersListParams): Promise<PinnedOffersListResponse> {
  const queryParams = {
    page,
    limit,
    ...(offer_country && { offer_country }),
    ...(offer_id && { offer_id }),
    brand,
  };

  const url = `${basePath()}/pinned-offers?${qs.stringify(queryParams)}`;
  return request(url, {
    method: 'GET',
  });
}

export async function createPinnedOffer({
  offer_id,
  min_length_of_stay,
  brand,
}: PinnedOffersCreateParams): Promise<PinnedOffersCreateResponse> {
  const url = `${basePath()}/pinned-offers`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({
      offer_id,
      ...(min_length_of_stay && { min_length_of_stay }),
      brand,
    }),
  });
}

export async function updatePinnedOffer(
  { min_length_of_stay, brand }: PinnedOffersUpdateParams,
  id: string,
): Promise<PinnedOffersUpdateResponse> {
  const url = `${basePath()}/pinned-offers/${id}`;
  return request(url, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify({
      min_length_of_stay,
      brand,
    }),
  });
}

export async function deletePinnedOffer(id: string): Promise<PinnedOffersDeleteResponse> {
  const url = `${basePath()}/pinned-offers/${id}`;
  return request(url, {
    method: 'DELETE',
  });
}

export type AdAssetViewItem =
  operations['getAdGroupAdAssetView']['responses']['200']['content']['application/json']['result'][0];

export type AdAssetViewResponse = {
  result: Array<AdAssetViewItem>;
};

export async function getAdAssetView(
  customerId: string,
  campaignId: string,
  adGroupId: string,
): Promise<AdAssetViewResponse> {
  const url = `${basePath()}/google-ads/customer/${customerId}/campaign/${campaignId}/ad-group/${adGroupId}/adAssetView`;
  return request(url, {
    method: 'GET',
    headers: json_headers,
  });
}
